// @shared

export type RoomAudioType = "PERSONAL" | "LIBRARY" | "CREATIVE" | "SOCIAL" | "COFFEE" | "COWORK" | "KPOP" | null

const STORAGE_KEY = "pomesto.audioSettings"
const BELL_AUDIO_URL = "https://sel.cdn-chatium.io/get/audio_msk_HC6myC0IWn.d5.mpga"
const LIBRARY_AUDIO_URL = "https://fs.chatium.ru/get/audio_msk_F4pHYc6TFD.d261.mpga"
const CREATIVE_AUDIO_URL = "https://fs.chatium.ru/get/audio_msk_ybMALQACcP.d91.mpga"
const COFFEE_AUDIO_URL = "https://fs.chatium.ru/get/audio_msk_9wl5Urd2U1.d311.mpga"
const COWORK_AUDIO_URL = "https://fs.chatium.ru/get/audio_msk_oRGho2IMWH.d206.mpga"

interface AudioSettings {
  muted: boolean
  volume: number
}

class AudioService {
  private audioContext: AudioContext | null = null
  private gainNode: GainNode | null = null
  private masterGain: GainNode | null = null
  private currentRoomType: RoomAudioType = null
  private activeNodes: AudioNode[] = []
  private intervals: number[] = []
  private settings: AudioSettings = { muted: false, volume: 0.5 }
  private hasUserInteraction = false
  private isPlaying = false
  private htmlAudioEl: HTMLAudioElement | null = null
  private mediaSource: MediaElementAudioSourceNode | null = null

  constructor() {
    this.loadSettings()
    this.setupInteractionListener()
  }

  private loadSettings() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        this.settings = { ...this.settings, ...JSON.parse(saved) }
      }
    } catch {
      // ignore
    }
  }

  private saveSettings() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings))
    } catch {
      // ignore
    }
  }

  private setupInteractionListener() {
    const markInteracted = () => {
      this.hasUserInteraction = true
      if (this.currentRoomType && !this.isPlaying) {
        this.resume()
      }
    }
    document.addEventListener("click", markInteracted, { once: true })
    document.addEventListener("keydown", markInteracted, { once: true })
    document.addEventListener("touchstart", markInteracted, { once: true })
  }

  private async initAudioContext(): Promise<void> {
    if (this.audioContext) return
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      this.masterGain = this.audioContext.createGain()
      this.masterGain.connect(this.audioContext.destination)
      this.updateGain()
    } catch (e) {
      console.warn("[AudioService] Web Audio API not supported:", e)
    }
  }

  private updateGain() {
    if (!this.masterGain || !this.audioContext) return
    const effectiveVolume = this.settings.muted ? 0 : this.settings.volume
    this.masterGain.gain.setTargetAtTime(effectiveVolume, this.audioContext.currentTime, 0.1)
  }

  private stopAllSounds(): void {
    // Останавливаем HTML Audio
    if (this.htmlAudioEl) {
      this.htmlAudioEl.pause()
      this.htmlAudioEl.src = ""
      this.htmlAudioEl = null
    }
    if (this.mediaSource) {
      try { this.mediaSource.disconnect() } catch {}
      this.mediaSource = null
    }

    // Очищаем интервалы
    this.intervals.forEach(id => clearInterval(id))
    this.intervals = []

    // Отключаем ноды
    this.activeNodes.forEach(node => {
      try {
        if (node instanceof OscillatorNode || node instanceof AudioBufferSourceNode) {
          node.stop()
        }
        node.disconnect()
      } catch {}
    })
    this.activeNodes = []
    this.isPlaying = false
  }

  // ===== LIBRARY / CREATIVE: воспроизведение файла =====
  private playFileAudio(url: string): void {
    if (!this.audioContext || !this.masterGain) return

    const audio = new Audio()
    audio.src = url
    audio.loop = true
    audio.crossOrigin = "anonymous"
    this.htmlAudioEl = audio

    try {
      const source = this.audioContext.createMediaElementSource(audio)
      source.connect(this.masterGain)
      this.mediaSource = source
    } catch (e) {
      // Если MediaElementSource уже создан или ошибка — просто воспроизводим напрямую
      console.warn("[AudioService] MediaElementSource error:", e)
    }

    audio.play().catch(e => console.warn("[AudioService] Audio play failed:", e))
  }

  // ===== CREATIVE: отдельный файл =====
  private playCreativeSounds(): void {
    this.playFileAudio(CREATIVE_AUDIO_URL)
  }

  // ===== COFFEE: атмосфера балийской кофейни =====
  private playCoffeeSounds(): void {
    this.playFileAudio(COFFEE_AUDIO_URL)
  }

  // ===== COWORK: атмосфера коворкинга =====
  private playCoworkSounds(): void {
    this.playFileAudio(COWORK_AUDIO_URL)
  }

  // ===== SOCIAL / КОМНАТА ОТДЫХА: процедурная спокойная атмосфера =====
  // Все звуки синтезируются Web Audio API. Сторонние записи не используются.
  private playRestSounds(): void {
    if (!this.audioContext || !this.masterGain) return

    const ctx = this.audioContext
    const master = this.masterGain

    // Очень тихий тёплый фон без широкополосного шума и резких верхних частот.
    const padGain = ctx.createGain()
    const padFilter = ctx.createBiquadFilter()
    padGain.gain.value = 0.007
    padFilter.type = "lowpass"
    padFilter.frequency.value = 520
    padFilter.Q.value = 0.45
    padFilter.connect(padGain)
    padGain.connect(master)

    const padOscillators = [
      { frequency: 130.81, detune: -4, level: 0.62 },
      { frequency: 196.0, detune: 3, level: 0.38 },
    ].map(({ frequency, detune, level }) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = "sine"
      osc.frequency.value = frequency
      osc.detune.value = detune
      gain.gain.value = level
      osc.connect(gain)
      gain.connect(padFilter)
      osc.start()
      return osc
    })

    // Почти незаметное «дыхание» громкости, чтобы фон не ощущался как гудок.
    const breath = ctx.createOscillator()
    const breathDepth = ctx.createGain()
    breath.type = "sine"
    breath.frequency.value = 0.055
    breathDepth.gain.value = 0.0018
    breath.connect(breathDepth)
    breathDepth.connect(padGain.gain)
    breath.start()
    this.activeNodes.push(padFilter, padGain, breath, breathDepth, ...padOscillators)

    const playSoftChime = () => {
      if (!this.audioContext || !this.masterGain || this.currentRoomType !== "SOCIAL") return
      const now = this.audioContext.currentTime
      const notes = [261.63, 329.63, 392.0]

      notes.forEach((frequency, index) => {
        const osc = this.audioContext!.createOscillator()
        const gain = this.audioContext!.createGain()
        const filter = this.audioContext!.createBiquadFilter()
        const start = now + index * 0.42
        const duration = 4.2 - index * 0.35

        osc.type = "sine"
        osc.frequency.value = frequency
        filter.type = "lowpass"
        filter.frequency.value = 1200
        filter.Q.value = 0.35
        gain.gain.setValueAtTime(0.0001, start)
        gain.gain.exponentialRampToValueAtTime(0.012 - index * 0.002, start + 0.08)
        gain.gain.exponentialRampToValueAtTime(0.0001, start + duration)

        osc.connect(filter)
        filter.connect(gain)
        gain.connect(this.masterGain!)
        osc.start(start)
        osc.stop(start + duration + 0.05)
        const nodes: AudioNode[] = [osc, filter, gain]
        this.activeNodes.push(...nodes)
        osc.addEventListener("ended", () => {
          nodes.forEach(node => {
            try { node.disconnect() } catch {}
            const activeIndex = this.activeNodes.indexOf(node)
            if (activeIndex >= 0) this.activeNodes.splice(activeIndex, 1)
          })
        }, { once: true })
      })
    }

    playSoftChime()
    this.intervals.push(window.setInterval(playSoftChime, 16000))
  }

  // ===== PUBLIC API =====

  setVolume(volume: number) {
    this.settings.volume = Math.max(0, Math.min(1, volume))
    this.saveSettings()
    this.updateGain()
  }

  toggleMute(): boolean {
    this.settings.muted = !this.settings.muted
    this.saveSettings()
    this.updateGain()
    return this.settings.muted
  }

  isMuted(): boolean {
    return this.settings.muted
  }

  getVolume(): number {
    return this.settings.volume
  }

  async playForRoom(roomType: RoomAudioType) {
    if (roomType === "PERSONAL") {
      this.currentRoomType = null
      this.stopAllSounds()
      return
    }
    this.currentRoomType = roomType

    await this.initAudioContext()

    if (!this.hasUserInteraction || !this.audioContext) {
      return
    }

    // Resume audio context если он suspended
    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume()
    }

    // Останавливаем текущие звуки
    this.stopAllSounds()

    if (!roomType) return

    this.isPlaying = true

    // Запускаем соответствующие звуки
    switch (roomType) {
      case "LIBRARY":
        this.playFileAudio(LIBRARY_AUDIO_URL)
        break
      case "CREATIVE":
        this.playCreativeSounds()
        break
      case "SOCIAL":
        this.playRestSounds()
        break
      case "COFFEE":
        this.playCoffeeSounds()
        break
      case "COWORK":
        this.playCoworkSounds()
        break
      case "KPOP":
        this.playCreativeSounds()
        break
    }

    // Применяем текущую громкость
    this.updateGain()
  }

  stop() {
    this.stopAllSounds()
    this.currentRoomType = null
    if (this.audioContext?.state === "running") {
      this.audioContext.suspend()
    }
  }

  resume() {
    if (this.currentRoomType && this.hasUserInteraction) {
      this.playForRoom(this.currentRoomType)
    }
  }

  get hasInteracted(): boolean {
    return this.hasUserInteraction
  }

  // ===== 8-bit SFX для мини-игр =====
  async playSfx8bit(kind: "eat" | "gameover"): Promise<void> {
    await this.initAudioContext()
    if (!this.audioContext || !this.masterGain) return
    if (this.settings.muted) return
    if (this.audioContext.state === "suspended") {
      try { await this.audioContext.resume() } catch {}
    }
    const ctx = this.audioContext
    const master = this.masterGain
    const now = ctx.currentTime

    if (kind === "eat") {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = "square"
      osc.frequency.setValueAtTime(880, now)
      osc.frequency.exponentialRampToValueAtTime(1760, now + 0.08)
      gain.gain.setValueAtTime(0.0001, now)
      gain.gain.exponentialRampToValueAtTime(0.25, now + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12)
      osc.connect(gain)
      gain.connect(master)
      osc.start(now)
      osc.stop(now + 0.14)
    } else {
      const notes: Array<[number, number]> = [
        [440, 0.0],
        [330, 0.12],
        [220, 0.26],
        [110, 0.42],
      ]
      for (const [freq, t] of notes) {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = "square"
        osc.frequency.value = freq
        gain.gain.setValueAtTime(0.0001, now + t)
        gain.gain.exponentialRampToValueAtTime(0.22, now + t + 0.01)
        gain.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.16)
        osc.connect(gain)
        gain.connect(master)
        osc.start(now + t)
        osc.stop(now + t + 0.2)
      }
    }
  }

  async playCaseSfx(kind: "tick" | "reveal"): Promise<void> {
    await this.initAudioContext()
    if (!this.audioContext || !this.masterGain || this.settings.muted) return
    if (this.audioContext.state === "suspended") {
      try { await this.audioContext.resume() } catch {}
    }
    const ctx = this.audioContext
    const now = ctx.currentTime
    const notes = kind === "tick"
      ? [{ frequency: 680, at: 0, duration: 0.055, volume: 0.08 }]
      : [
          { frequency: 392, at: 0, duration: 0.22, volume: 0.13 },
          { frequency: 523.25, at: 0.11, duration: 0.3, volume: 0.15 },
          { frequency: 659.25, at: 0.22, duration: 0.42, volume: 0.18 },
        ]
    for (const note of notes) {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = kind === "tick" ? "square" : "sine"
      osc.frequency.setValueAtTime(note.frequency, now + note.at)
      gain.gain.setValueAtTime(0.0001, now + note.at)
      gain.gain.exponentialRampToValueAtTime(note.volume, now + note.at + 0.012)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + note.at + note.duration)
      osc.connect(gain)
      gain.connect(this.masterGain)
      osc.start(now + note.at)
      osc.stop(now + note.at + note.duration + 0.03)
    }
  }

  // ===== Звонок по завершении Pomodoro =====
  async playBell(): Promise<void> {
    await this.initAudioContext()
    if (!this.audioContext || !this.masterGain) return
    if (this.audioContext.state === "suspended") {
      try { await this.audioContext.resume() } catch {}
    }
    const audio = new Audio(BELL_AUDIO_URL)
    audio.volume = this.settings.muted ? 0 : Math.max(0.3, this.settings.volume)
    audio.play().catch(e => console.warn("[AudioService] Bell play failed:", e))
  }
}

export const audioService = new AudioService()
